import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import NewsletterForm from "./NewsletterForm";

export const Footer = () => {
  const { t } = useTranslation();

  const links = [
    { to: "/", labelKey: "nav.home", testid: "home" },
    { to: "/services", labelKey: "nav.services", testid: "services" },
    { to: "/about", labelKey: "nav.about", testid: "about" },
    { to: "/portfolio", labelKey: "nav.portfolio", testid: "portfolio" },
    { to: "/limited-edition", labelKey: "nav.limited_edition", testid: "limited-edition" },
    { to: "/contact", labelKey: "nav.contact", testid: "contact" },
  ];

  return (
    <footer
      data-testid="footer"
      className="border-t border-[#D8D4CC]/40 relative overflow-hidden"
      style={{ backgroundColor: "#F3F1EC" }}
    >
      {/* Newsletter band */}
      <section
        data-testid="newsletter-band"
        className="border-b border-[#D8D4CC]/40 grain-overlay relative"
        style={{ backgroundColor: "#FAF9F6" }}
      >
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 py-16 lg:py-24 text-center">
          <p
            className="text-xs uppercase tracking-[0.25em] text-[#C4A462] mb-6"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
          >
            ETERNA Newsletter
          </p>
          <div className="luxury-divider mx-auto mb-6" />
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1B1A] mb-6 leading-[1.05]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            {t("footer.newsletter_title_long_part_1", "Storie d'epoca")},{" "}
            <span style={{ fontWeight: 600, fontStyle: "italic" }}>
              {t("footer.newsletter_title_long_part_2", "in anteprima.")}
            </span>
          </h2>
          <p
            className="text-sm sm:text-base text-[#4A4A4A] leading-relaxed max-w-xl mx-auto mb-10"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            {t("footer.newsletter_subtitle")}
          </p>
          <div className="max-w-md mx-auto">
            <NewsletterForm />
          </div>
          <p
            className="text-[10px] uppercase tracking-[0.2em] text-[#757575] mt-6"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
          >
            {t("footer.newsletter_legal", "Nessuno spam. Disiscrizione con un click.")}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
          <div className="md:col-span-6">
            <h3
              className="text-3xl tracking-tight text-[#1A1B1A] mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            >
              ETERNA <span className="font-light">Motion</span>
            </h3>
            <p
              className="text-sm text-[#4A4A4A] leading-relaxed max-w-md"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
            >
              {t("footer.tagline")}
            </p>
          </div>

          <div className="md:col-span-3">
            <p
              className="text-xs uppercase tracking-[0.2em] text-[#757575] mb-6"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("footer.navigation")}
            </p>
            <div className="flex flex-col gap-4">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`footer-link-${link.testid}`}
                  className="text-sm text-[#4A4A4A] hover:text-[#C4A462] transition-colors duration-300"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
                >
                  {t(link.labelKey)}
                </Link>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <p
              className="text-xs uppercase tracking-[0.2em] text-[#757575] mb-6"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("footer.contacts")}
            </p>
            <div className="flex flex-col gap-3 text-sm text-[#4A4A4A]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              <p>info@eternamotion.com</p>
              <p>+39 348 270 4976</p>
              <p>{t("contact.info_address_value")}</p>
            </div>

            <div className="flex gap-5 mt-8">
              {[
                { icon: Instagram, label: "instagram", href: "#" },
                { icon: Facebook, label: "facebook", href: "#" },
                { icon: Linkedin, label: "linkedin", href: "#" },
              ].map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  data-testid={`social-${label}`}
                  className="text-[#757575] hover:text-[#C4A462] transition-colors duration-300"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                >
                  <Icon strokeWidth={1.25} size={20} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#D8D4CC]/40 flex flex-col md:flex-row justify-between items-center gap-4">
          <p
            className="text-xs text-[#757575]"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            &copy; {new Date().getFullYear()} ETERNA Motion. {t("footer.rights")}
          </p>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="text-xs uppercase tracking-[0.15em] text-[#757575] hover:text-[#C4A462] transition-colors duration-300"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("footer.privacy")}
            </Link>
            <span className="text-[#D8D4CC] text-xs">·</span>
            <Link
              to="/cookie"
              data-testid="footer-cookie-link"
              className="text-xs uppercase tracking-[0.15em] text-[#757575] hover:text-[#C4A462] transition-colors duration-300"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("footer.cookie")}
            </Link>
            <span className="text-[#D8D4CC] text-xs">·</span>
            <a
              href="#top"
              className="text-xs uppercase tracking-[0.15em] text-[#757575] hover:text-[#C4A462] flex items-center gap-1 transition-colors duration-300"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t("footer.back_to_top")} <ArrowUpRight strokeWidth={1.25} size={14} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
